import { useEffect, useState } from "react";
import api from "../lib/api";
import { ASSETS, RECURRING_SERVICES, nextOccurrence } from "../lib/assets";

function fmt(date) {
  return new Date(date).toLocaleString("es-ES", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function clockNow() {
  return new Date().toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export default function TvSlideshow() {
  const [events, setEvents] = useState([]);
  const [idx, setIdx] = useState(0);
  const [now, setNow] = useState(clockNow());

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(clockNow()), 30_000);
    return () => clearInterval(t);
  }, []);

  // Build the slide list: featured + alternate flyer + recurring services + any other admin events.
  const recurring = RECURRING_SERVICES.map((s) => {
    const next = nextOccurrence(s.weekday);
    next.setHours(s.startHour, 0, 0, 0);
    return {
      id: s.id,
      kind: "recurring",
      title: s.title,
      subtitle: s.weekdayLabel,
      time_text: s.timeLabel,
      location: s.location,
      description: s.description,
      bg: ASSETS.scrantonBg,
      accent: s.accent,
      next_date: next,
    };
  });

  const specials = events.flatMap((e) => {
    const base = {
      id: e.id,
      kind: "special",
      title: e.title,
      subtitle: e.featured ? "Evento destacado" : "Evento especial",
      time_text: fmt(e.event_date),
      location: e.location,
      description: e.description,
      bg: e.image_url || ASSETS.scrantonBg,
      accent: "primary",
      next_date: new Date(e.event_date),
    };
    // For featured events, also show the alternate flyer as a separate slide.
    if (e.featured) {
      return [
        base,
        { ...base, id: `${e.id}-alt`, bg: ASSETS.flyerB },
      ];
    }
    return [base];
  });

  const slides = [...specials, ...recurring];

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 10_000);
    return () => clearInterval(t);
  }, [slides.length]);

  if (!slides.length)
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        Cargando…
      </div>
    );

  const s = slides[idx];

  return (
    <div data-testid="tv-page" className="fixed inset-0 bg-black overflow-hidden">
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-[1200ms] ${
            i === idx ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={slide.bg}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/55 to-[color:var(--inv-primary)]/45" />
        </div>
      ))}

      <div className="relative h-full flex flex-col justify-between p-14 md:p-24 lg:p-28 text-white">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <img
              src={ASSETS.churchLogo}
              alt="Logo"
              className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover ring-2 ring-white/30"
            />
            <div>
              <div className="font-heading text-2xl md:text-4xl font-semibold tracking-tight">
                Iglesia Nueva Vida Internacional
              </div>
              <div className="text-white/70 text-sm md:text-lg mt-1">
                120 S Main Ave, Scranton, PA · invispa.com
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.3em] text-white/60">Modo TV</div>
            <div className="font-heading text-4xl md:text-6xl font-semibold tracking-tight whitespace-nowrap">
              {now}
            </div>
          </div>
        </div>

        {/* Slide content */}
        <div className="max-w-6xl">
          <div
            className={`inline-flex items-center px-5 py-2 rounded-full text-xs md:text-base uppercase tracking-[0.25em] font-semibold mb-8 ${
              s.kind === "special"
                ? "bg-[color:var(--inv-primary)] text-white"
                : "bg-white/15 backdrop-blur-md text-white"
            }`}
          >
            {s.subtitle}
          </div>
          <h1 className="font-heading text-5xl md:text-8xl font-semibold tracking-tight leading-[0.95]">
            {s.title}
          </h1>
          <div className="mt-10 flex flex-wrap gap-x-12 gap-y-5 text-xl md:text-3xl text-white/90 font-medium">
            <span>📅 {s.time_text}</span>
            <span>📍 {s.location}</span>
          </div>
          {s.description && (
            <p className="mt-10 max-w-4xl text-lg md:text-2xl text-white/80 leading-relaxed line-clamp-3">
              {s.description}
            </p>
          )}
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2.5">
          {slides.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "w-16 bg-white" : "w-3 bg-white/30"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
